import { useState, useEffect, useMemo } from 'react'
import BotTrades from './BotTrades'
import BotLogs from './BotLogs'
import BacktestBottomPanel from './BacktestBottomPanel'
import type { BottomTab } from './BacktestBottomPanel'
import BotBacktestNewResults from './BotBacktestNewResults'
import BotBacktestTopSection from './BotBacktestTopSection'
import useBacktestChartState from '../hooks/useBacktestChartState'
import ResizableVerticalPanel from '../../components-legacy/ResizableVerticalPanel'
import { botApi, type BacktestGraphData, type BacktestResult, type BacktestResultData, type BotCode } from '../api'
import { parseTimestampMs } from '../utils/timeParsing'

interface BotBacktestMainProps {
  btid: string
  backtest: BacktestResult
  currentCode?: BotCode | null
  hasCode?: boolean
  activeBottomTab?: BottomTab
  onActiveBottomTabChange?: (tab: BottomTab) => void
}

interface BacktestConfig {
  symbol: string
  timeframe: string
  start_date: string
  end_date: string
}

function parseBacktestConfig(backtest: BacktestResult): BacktestConfig | null {
  const bc = backtest.configInput?.backtest_config
  if (!bc || !bc.symbol || !bc.start_date || !bc.end_date) return null
  return {
    symbol: bc.symbol,
    // Charted at the timeframe the strategy actually ran on — the WebSocket
    // datafeed serves all of them, so there's no longer any need to collapse
    // to the three the old per-day HTTP endpoint supported.
    timeframe: (bc.timeframe || 'H1').toUpperCase(),
    start_date: bc.start_date,
    end_date: bc.end_date,
  }
}


export default function BotBacktestMain({
  btid,
  backtest,
  currentCode,
  hasCode,
  activeBottomTab,
  onActiveBottomTabChange,
}: BotBacktestMainProps) {
  const config = parseBacktestConfig(backtest)
  if (!config) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: 'var(--color-error)' }}
      >
        Invalid backtest configuration
      </div>
    )
  }

  return (
    <BotBacktestMainInner
      btid={btid}
      config={config}
      initialStatus={backtest.status}
      initialProgressBacktest={backtest.progressBacktest ?? null}
      initialProgressView={backtest.progressView ?? null}
      backtestConfigInput={backtest.configInput}
      backtestCreated={backtest.created}
      currentCode={currentCode}
      hasCode={hasCode}
      controlledActiveBottomTab={activeBottomTab}
      onActiveBottomTabChange={onActiveBottomTabChange}
    />
  )
}

interface BotBacktestMainInnerProps {
  btid: string
  config: BacktestConfig
  initialStatus: number
  initialProgressBacktest: string | null
  initialProgressView: string | null
  backtestConfigInput: BacktestResult['configInput']
  backtestCreated: string
  currentCode?: BotCode | null
  hasCode?: boolean
  controlledActiveBottomTab?: BottomTab
  onActiveBottomTabChange?: (tab: BottomTab) => void
}

function BotBacktestMainInner({
  btid,
  config,
  initialStatus,
  initialProgressBacktest,
  initialProgressView,
  backtestConfigInput,
  backtestCreated,
  currentCode,
  hasCode = false,
  controlledActiveBottomTab,
  onActiveBottomTabChange,
}: BotBacktestMainInnerProps) {
  const symbol = config.symbol
  const [isBottomMaximized, setIsBottomMaximized] = useState(false)
  const [internalActiveBottomTab, setInternalActiveBottomTab] = useState<BottomTab>('results')
  const activeBottomTab = controlledActiveBottomTab ?? internalActiveBottomTab
  const setActiveBottomTab = (tab: BottomTab) => {
    if (controlledActiveBottomTab === undefined) {
      setInternalActiveBottomTab(tab)
    }
    onActiveBottomTabChange?.(tab)
  }
  const [graphData, setGraphData] = useState<BacktestGraphData | null>(null)
  const [resultsData, setResultsData] = useState<BacktestResultData | null>(null)
  const rangeStart = useMemo(() => new Date(`${config.start_date}T00:00:00Z`), [config.start_date])
  const rangeEnd = useMemo(() => new Date(`${config.end_date}T23:59:59Z`), [config.end_date])
  const rangeStartTs = useMemo(() => Math.floor(rangeStart.getTime() / 1000), [rangeStart])
  const rangeEndTs = useMemo(() => Math.floor(rangeEnd.getTime() / 1000), [rangeEnd])
  const backtestStartedAtMs = useMemo(() => parseTimestampMs(backtestCreated), [backtestCreated])

  const {
    timeframe,
    isPlaying,
    speed,
    isBacktestCompleted,
    candles,
    isLoading,
    loadError,
    orders,
    chartRef,
    handleVisibleRangeChange,
    maxAvailableTs,
    isInProgress,
    progressPercent,
    progressViewPosition,
    equityDisplayBoundaryTs,
    currentTimestamp,
    equityPoints,
    equityRunningTotals,
    isPlayDisabled,
    handleReset,
    handlePlay,
    handlePause,
    handleChartUserNavigate,
    handleSpeedChange,
    handleCycleSpeed,
    handleTimeframeChange,
    handleEquityTimeClick,
    playStartedAtMs,
    playElapsedSec,
    progressViewTs,
    tickReplay,
  } = useBacktestChartState({
    btid,
    symbol,
    initialTimeframe: config.timeframe,
    initialStatus,
    initialProgressBacktest,
    initialProgressView,
    rangeStartTs,
    rangeEndTs,
  })

  useEffect(() => {
    if (!isBacktestCompleted || graphData) return
    // The new Results tab needs the aggregated graph data (monthly P&L,
    // day-of-week, yearly returns, MFE/MAE) once the backtest is complete.
    if (activeBottomTab !== 'results') return

    let cancelled = false
    botApi.getBacktestGraphData('', btid)
      .then(data => {
        if (cancelled) return
        setGraphData(data)
      })
      .catch(() => {
        // BotBacktestNewResults renders empty/pending states gracefully when
        // graphData is null, so we don't need to surface an error UI here.
      })

    return () => {
      cancelled = true
    }
  }, [isBacktestCompleted, graphData, btid, activeBottomTab])

  useEffect(() => {
    if (!isBacktestCompleted || resultsData) return
    if (activeBottomTab !== 'results') return

    let cancelled = false
    botApi.getBacktestResults(btid)
      .then(data => {
        if (cancelled) return
        setResultsData(data)
      })
      .catch(() => {
        // BotBacktestNewResults handles missing resultsData with its skeleton /
        // running-aggregate fallbacks; no error UI is surfaced from here.
      })

    return () => {
      cancelled = true
    }
  }, [isBacktestCompleted, resultsData, btid, activeBottomTab])

  const initialBalanceForResults =
    typeof backtestConfigInput?.backtest_config?.initial_balance === 'number'
      ? backtestConfigInput.backtest_config.initial_balance
      : 10000

  const newResultsContent = (
    <BotBacktestNewResults
      equityPoints={equityPoints}
      equityRunningTotals={equityRunningTotals}
      resultsData={resultsData}
      graphData={graphData}
      isBacktestCompleted={isBacktestCompleted}
      initialBalance={initialBalanceForResults}
      maxAvailableTs={maxAvailableTs ?? undefined}
      equityDisplayBoundaryTs={equityDisplayBoundaryTs}
      currentTimestamp={currentTimestamp}
      rangeStartTs={rangeStartTs}
      rangeEndTs={rangeEndTs}
      onTimeClick={handleEquityTimeClick}
    />
  )

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--base-100)' }}>
      <div className="flex-1 min-h-0">
        {loadError && candles.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 h-full px-4 text-center">
            <p className="text-body text-danger">Couldn&apos;t load price data</p>
            <p className="text-caption text-muted-foreground">{loadError}</p>
          </div>
        ) : isLoading && candles.length === 0 ? (
          <div
            className="flex items-center justify-center h-full text-sm"
            style={{ color: 'var(--color-text-grey)' }}
          >
            Loading chart data...
          </div>
        ) : isBottomMaximized ? (
          <BacktestBottomPanel
            tradesContent={
              <BotTrades
                btid={btid}
                onTimeClick={handleEquityTimeClick}
              />
            }
            newResultsContent={newResultsContent}
            logsContent={<BotLogs btid={btid} />}
            currentCode={currentCode}
            hasCode={hasCode}
            activeTab={activeBottomTab}
            onActiveTabChange={setActiveBottomTab}
            isMaximized={true}
            onToggleMaximize={() => setIsBottomMaximized(false)}
            showUpgradeLock
          />
        ) : (
          <ResizableVerticalPanel
            defaultTopHeight={55}
            minTopHeight={25}
            maxTopHeight={80}
            storageKey="backtest-chart-split"
          >
            <BotBacktestTopSection
              symbol={symbol}
              timeframe={timeframe}
              startDate={config.start_date}
              endDate={config.end_date}
              speed={speed}
              isPlaying={isPlaying}
              isPlayDisabled={isPlayDisabled}
              candles={candles}
              orders={orders}
              maxAvailableTs={maxAvailableTs}
              chartRef={chartRef}
              configInput={backtestConfigInput}
              onVisibleRangeChange={handleVisibleRangeChange}
              onUserNavigate={handleChartUserNavigate}
              onReset={handleReset}
              onPlay={handlePlay}
              onPause={handlePause}
              onTimeframeChange={handleTimeframeChange}
              onSpeedChange={handleSpeedChange}
              onCycleSpeed={handleCycleSpeed}
              onScrubTime={handleEquityTimeClick}
              isInProgress={isInProgress}
              isBacktestCompleted={isBacktestCompleted}
              progressPercent={progressPercent}
              progressViewPosition={progressViewPosition}
              rangeStartTs={rangeStartTs}
              rangeEndTs={rangeEndTs}
              backtestStartedAtMs={backtestStartedAtMs}
              playStartedAtMs={playStartedAtMs}
              playElapsedSec={playElapsedSec}
              progressViewTs={progressViewTs}
              tickReplay={tickReplay}
            />
            <BacktestBottomPanel
              tradesContent={
                <BotTrades
                  btid={btid}
                  onTimeClick={handleEquityTimeClick}
                />
              }
              newResultsContent={newResultsContent}
              logsContent={<BotLogs btid={btid} />}
              currentCode={currentCode}
              hasCode={hasCode}
              activeTab={activeBottomTab}
              onActiveTabChange={setActiveBottomTab}
              isMaximized={false}
              onToggleMaximize={() => setIsBottomMaximized(true)}
              showUpgradeLock
            />
          </ResizableVerticalPanel>
        )}
      </div>
    </div>
  )
}
